import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { Suspension } from '@adornis/buchungsheld-db/entities/suspension.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { filter, map } from 'rxjs';

@customElement('bh-prompt-suspend-user')
export class BHPromptSuspendUser extends TrDialog<void> {
  @property({ attribute: false }) user!: User;
  @property({ attribute: false, reflect: true }) institutionID = new RXController<undefined | string>(this, undefined);

  static override get element_name() {
    return 'bh-prompt-suspend-user';
  }

  private readonly _suspensionController = new FormController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      map(id => new Suspension({ institutionID: id })),
    ),
    {
      success: async doc => {
        await XDialog.waitFor(
          User.suspendUserFromInstitution(this.user._id, doc)(),
          'Benutzer wird suspendiert...',
        ).then(res => this.close());
      },
      error: err => {
        return XSnackbar.show(err.message);
      },
    },
  );

  override render() {
    if (!this._suspensionController.document || !this.user || this.institutionID.isLoading || !this.institutionID.value)
      return html` ${this._translation.translate('loading')} `;

    return html`
      <x-flex space="md" padding="md">
        <x-text bold>${this._translation.translate('PROMPT', 'suspension_of')} ${this.user.email}</x-text>
        <x-textarea placeholder="Notiz" ${this._suspensionController.field('message')}></x-textarea>

        <x-text>${this._translation.translate('PROMPT', 'suspension_duration')}</x-text>
        <x-checkbox
          .label=${this._translation.translate('PROMPT', 'suspension_permanent')}
          ${this._suspensionController.field('blockedNoLimit')}
          @value-picked=${(e: ValueEvent<boolean>) => {
            if (e.detail.value) this._suspensionController.document!.blockedTill = null;
            this.requestUpdate();
          }}
        ></x-checkbox>

        ${this._suspensionController.document.blockedNoLimit
          ? nothing
          : html`
              <x-input
                type="date"
                ${this._suspensionController.field('blockedTill')}
                @value-picked=${(e: ValueEvent<boolean>) => {
                  this.requestUpdate();
                }}
              ></x-input>
            `}

        <x-flex horizontal crossaxis-center space-between wrap>
          <x-button
            mode="outline"
            @click=${() => {
              return this.cancel();
            }}
          >
            ${this._translation.translate('discard')}
          </x-button>
          <x-button
            @click=${() => {
              return this._suspensionController.validate();
            }}
          >
            ${this._translation.translate('save')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }
}
